import { useMemo } from "react";
import { SegmentedTabSwitcher } from "@aera/ui";
import type { DataTableTheme, WorkerSchema, WorkspaceConfig } from "../types";
import { buildDataTableThemeVars } from "../utils/theme";
import {
  normalizeWorkerTab,
  resolveWorkerIconKey,
  stripWorkerLabelEmoji,
} from "../utils/workerIcons";
import {
  resolveIconComponent,
  type IconOverrideMap,
} from "../utils/iconRegistry";

export interface WorkerTab {
  id: string;
  label: string;
  live: boolean;
  iconKey?: string;
}

export interface WorkerTabSwitcherProps {
  activeWorker: string;
  onSwitch: (workerId: string) => void;
  workspaceConfig: WorkspaceConfig;
  themeOverrides?: Partial<DataTableTheme>;
  iconOverrides?: IconOverrideMap;
  /** Pass registered schemas so tabs are driven by what's actually available.
   *  Falls back to built-in default list if omitted. */
  schemas?: Record<string, WorkerSchema>;
  /** Extra tabs appended after schema-driven worker tabs. */
  appendTabs?: WorkerTab[];
  /** Mockups at 912/1024 use text-only worker tabs. */
  showIcons?: boolean;
}

const DEFAULT_TABS: WorkerTab[] = [
  { id: "bill-lin-income", label: "💰 รายรับ", live: true, iconKey: "wallet" },
  {
    id: "bill-lin-expense",
    label: "💸 รายจ่าย",
    live: true,
    iconKey: "receipt",
  },
  { id: "nad-dee", label: "📅 นัดหมาย", live: false, iconKey: "calendar" },
  { id: "song-dee", label: "📦 จัดส่ง", live: false, iconKey: "package" },
  { id: "erin", label: "💬 ลูกค้า", live: false, iconKey: "message-circle" },
];

export function WorkerTabSwitcher({
  activeWorker,
  onSwitch,
  workspaceConfig,
  schemas,
  appendTabs,
  themeOverrides,
  iconOverrides,
  showIcons = true,
}: WorkerTabSwitcherProps) {
  const tabs = useMemo(() => {
    const workers: WorkerTab[] = schemas
      ? Object.values(schemas).map((s) => ({
          ...normalizeWorkerTab(s),
          live: true,
        }))
      : DEFAULT_TABS.filter((w) => w.live || workspaceConfig.showSoon).map(
          (w) => ({
            ...w,
            label: stripWorkerLabelEmoji(w.label),
            iconKey: resolveWorkerIconKey({
              workerId: w.id,
              workerLabel: w.label,
              tabIcon: w.iconKey,
            }),
          }),
        );

    const allWorkers = [...workers, ...(appendTabs ?? [])];

    return allWorkers.map((worker) => ({
      id: worker.id,
      label: worker.label,
      icon: showIcons
        ? resolveIconComponent(worker.iconKey, iconOverrides)
        : undefined,
      disabled: !worker.live,
      trailing: !worker.live ? (
        <span className="text-[9px] px-1.5 py-0.5 bg-aera-warmcream text-aera-amber rounded-md font-bold uppercase tracking-tighter">
          Soon
        </span>
      ) : undefined,
    }));
  }, [appendTabs, schemas, workspaceConfig.showSoon, iconOverrides, showIcons]);

  return (
    <SegmentedTabSwitcher
      tabs={tabs}
      activeTab={activeWorker}
      onChange={onSwitch}
      ariaLabel="ประเภทรายการธุรกรรม"
      className="h-full"
      style={buildDataTableThemeVars(themeOverrides)}
    />
  );
}
